export type ClientErrorReportKind =
  | "react_error"
  | "global_error"
  | "window_error"
  | "unhandled_rejection";

const STORAGE_KEY = "jorvio_error_report_dedupe";
const DEDUPE_MS = 45 * 60 * 1000;

type DedupeEntry = { fp: string; t: number };

function loadDedupe(): DedupeEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is DedupeEntry => typeof x?.fp === "string" && typeof x?.t === "number");
  } catch {
    return [];
  }
}

function saveDedupe(entries: DedupeEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-40)));
  } catch {
    /* ignore */
  }
}

function fingerprintFor(
  kind: ClientErrorReportKind,
  message: string,
  stack?: string,
  reason?: string,
): string {
  const s = (stack ?? "").split("\n").slice(0, 3).join("|");
  /** Include `reason` so reports coming from different surfaces (e.g. our class
   *  error boundaries that tag themselves with `surface:proposals/[id]`) don't
   *  dedupe against earlier reports from app/error.tsx that have no surface tag
   *  — those earlier reports lack a componentStack and we still want the tagged
   *  diagnostic version through. */
  return `${kind}:${reason ?? ""}:${message.slice(0, 160)}:${s.slice(0, 240)}`;
}

const IGNORED_PATTERNS = [
  /lock.*was released because another request stole it/i,
];

function shouldSkip(fingerprint: string): boolean {
  const now = Date.now();
  const entries = loadDedupe().filter((e) => now - e.t < DEDUPE_MS);
  if (entries.some((e) => e.fp === fingerprint)) return true;
  entries.push({ fp: fingerprint, t: now });
  saveDedupe(entries);
  return false;
}

/**
 * Sends a single support email (via API + Resend) describing a client-side failure.
 * Dedupes identical fingerprints within one browser session to avoid inbox spam.
 */
export async function reportClientErrorToSupport(payload: {
  kind: ClientErrorReportKind;
  message: string;
  stack?: string;
  digest?: string;
  componentStack?: string;
  reason?: string;
}): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const msg = payload.message + (payload.stack ?? "");
  if (IGNORED_PATTERNS.some((re) => re.test(msg))) return false;

  /** Reports carrying a `componentStack` come from a class boundary we explicitly
   *  placed to diagnose a specific in-flight bug. They're high-signal — we want
   *  the first one through even if an earlier non-diagnostic report from
   *  app/error.tsx already deduped this fingerprint. The fingerprint includes
   *  `reason`, so a later non-diagnostic report still dedupes against itself,
   *  and a later diagnostic report still dedupes against itself. */
  const fp = fingerprintFor(payload.kind, payload.message, payload.stack, payload.reason);
  if (shouldSkip(fp)) return false;

  try {
    const res = await fetch("/api/support/report-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind: payload.kind,
        message: payload.message.slice(0, 4000),
        stack: payload.stack?.slice(0, 12000),
        digest: payload.digest,
        componentStack: payload.componentStack?.slice(0, 8000),
        reason: payload.reason?.slice(0, 2000),
        url: window.location.href.slice(0, 2000),
        pathname: window.location.pathname.slice(0, 500),
        userAgent: navigator.userAgent?.slice(0, 500),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
